import { List, Map } from "immutable";

import { ContentType } from "@seneca/course-service-types";
import { addToMean } from "@seneca/session-aggregation";

import AverageStats from "seneca-common/features/assignments/features/student-assignments/state/models/AverageStats";
import SectionStat from "seneca-common/features/assignments/features/student-assignments/state/models/SectionStat";
import { SpecType } from "seneca-common/features/assignments/state";
import CourseSectionsSpec from "seneca-common/features/assignments/state/models/spec/CourseSectionsSpec";
import { getSectionContentIds } from "seneca-common/features/assignments/state/selectors/state/assignments/assignment/spec/courseSectionsSpec";
import makeAssignmentReducer from "seneca-common/features/assignments/utils/reducers/makeAssignmentReducer";
import { Contents } from "seneca-common/features/course/state";
import { formatDateTimeString } from "seneca-common/utils/functions/time";

import actionTypes from "../../../../action-types";
import ContentStat, { ContentStatType } from "../../../../models/ContentStat";
import QuestionStat from "../../../../models/QuestionStat";
import QuizDetails from "../../../../models/QuizDetails";
import QuizModuleDetails from "../../../../models/QuizDetails/QuizModuleDetails";
import QuizStats from "../../../../models/QuizStat";
import QuizModuleStat from "../../../../models/QuizStat/QuizModuleStat";
import StudentAssignment from "../../../../models/StudentAssignment";
import { ContentStat as ContentStatReceived } from "../../../../models/StudentAssignmentReceived";
import VocabularyWordStat from "../../../../models/VocabularyWordStat";

const studentAssignmentReducer = makeAssignmentReducer({
  makeAssignment: (values = {}) => {
    const { assignment, contents } = values!;
    const sectionStatsMap =
      assignment?.sectionStats &&
      assignment.sectionStats.reduce(
        (acc: any, { sectionId, ...rest }: any) => {
          return acc.set(sectionId, new SectionStat(rest));
        },
        Map()
      );

    const questionStatMap =
      assignment?.questionStats &&
      assignment.questionStats?.reduce(
        (acc: any, { questionId, ...rest }: any) => {
          return acc.set(questionId, new QuestionStat(rest));
        },
        Map()
      );

    const quizStatMap =
      assignment?.quizStats &&
      assignment.quizStats?.reduce((acc: any, { quizId, ...rest }: any) => {
        return acc.set(
          quizId,
          new QuizStats({
            ...rest,
            moduleStats: Object.entries(rest.moduleStats).reduce(
              (acc: any, [moduleId, moduleStats]: [string, any]) => {
                return acc.set(moduleId, new QuizModuleStat(moduleStats));
              },
              Map()
            )
          })
        );
      }, Map());

    const quizDetailsMap =
      assignment?.quizDetails &&
      assignment.quizDetails.reduce((acc: any, { quizId, ...rest }: any) => {
        return acc.set(
          quizId,
          new QuizDetails({
            ...rest,
            modules: rest.modules.reduce(
              (acc: any, module: any) =>
                acc.set(module.moduleId, new QuizModuleDetails(module)),
              Map()
            )
          })
        );
      }, Map());

    const vocabularyStatsMap =
      assignment?.vocabularyStats &&
      assignment.vocabularyStats.reduce(
        (acc: any, { wordId, ...rest }: any) => {
          return acc.set(wordId, new VocabularyWordStat(rest));
        },
        Map()
      );

    return new StudentAssignment({
      ...assignment,
      sectionStats: sectionStatsMap,
      contentStats: aggregateContentStats(assignment?.contentStats),
      questionStats: questionStatMap,
      quizStats: quizStatMap,
      quizDetails: quizDetailsMap,
      vocabularyStats: vocabularyStatsMap,
      averageStats: new AverageStats(assignment?.averageStats),
      startDate: formatDateTimeString(assignment?.startDate),
      dueDate: formatDateTimeString(assignment?.dueDate),
      assignmentCutoffDate: formatDateTimeString(
        assignment?.assignmentCutoffDate
      ),
      contentTypesBySectionId: getContentTypesBySectionId(
        assignment?.spec,
        contents
      )
    });
  },
  receiveActionType: actionTypes.RECEIVE_ASSIGNMENT
});

export default studentAssignmentReducer;

function aggregateContentStats(contentStats: ContentStatReceived[]) {
  return contentStats?.reduce((acc, stat) => {
    if (acc.has(stat.sectionId)) {
      if (acc.get(stat.sectionId)?.has(stat.contentType)) {
        return acc.updateIn(
          [stat.sectionId, stat.contentType],
          existingStat =>
            new ContentStat({
              ...((existingStat as ContentStat)?.toJS() as ContentStatType),
              totalStudyTime:
                (existingStat as ContentStat)?.totalStudyTime ??
                0 + stat.totalStudyTime,
              bestScore: addToMean(
                (existingStat as ContentStat)?.bestScore,
                (existingStat as ContentStat)?.contentIds.size,
                stat.bestScore
              ),
              worstScore: addToMean(
                (existingStat as ContentStat)?.worstScore,
                (existingStat as ContentStat)?.contentIds.size,
                stat.worstScore
              ),
              averageScore: addToMean(
                (existingStat as ContentStat)?.averageScore,
                (existingStat as ContentStat)?.contentIds.size,
                stat.averageScore
              ),
              contentIds: (existingStat as ContentStat)?.contentIds.concat(
                stat.contentId
              )
            })
        );
      } else {
        return acc.updateIn([stat.sectionId], existingStats =>
          (existingStats as Map<ContentType, ContentStat>).set(
            stat.contentType,
            new ContentStat({
              bestScore: stat.bestScore,
              worstScore: stat.worstScore,
              averageScore: stat.averageScore,
              firstScore: stat.firstScore,
              lastScore: stat.lastScore,
              sessionsCompleted: stat.sessionsCompleted,
              totalStudyTime: stat.totalStudyTime,
              contentIds: List([stat.contentId])
            })
          )
        );
      }
    } else {
      return acc.set(
        stat.sectionId,
        Map<ContentType, ContentStat>({
          [stat.contentType ?? ContentType.standard]: new ContentStat({
            bestScore: stat.bestScore,
            worstScore: stat.worstScore,
            averageScore: stat.averageScore,
            firstScore: stat.firstScore,
            lastScore: stat.lastScore,
            sessionsCompleted: stat.sessionsCompleted,
            totalStudyTime: stat.totalStudyTime,
            contentIds: List([stat.contentId])
          })
        })
      );
    }
  }, Map<string, Map<ContentType, ContentStat>>());
}

function getContentTypesBySectionId(spec: SpecType, contents: Contents) {
  const sectionContentIds = getSectionContentIds(spec as CourseSectionsSpec);

  if (!sectionContentIds || sectionContentIds.size === 0) {
    return (
      spec
        ?.get("sectionIds", List())
        .reduce(
          (acc, sectionId) =>
            acc.set(sectionId as string, List([ContentType.standard as any])),
          Map()
        ) ?? (Map() as Map<string, List<ContentType>>)
    );
  }

  return sectionContentIds.reduce(
    (acc, contentIds, sectionId) =>
      acc.set(
        sectionId,
        contentIds
          .map(
            contentId =>
              contents?.find(content => content.id === contentId)?.contentType
          )
          .filter((contentType): contentType is ContentType => !!contentType)
          .toSet()
          .toList()
      ),
    Map()
  ) as Map<string, List<ContentType>>;
}
